import {
    FETCH_BANK_USER_DETAILS,
    FETCH_BANK_USER_DETAILS_SUCCESS,
    FETCH_BANK_USER_DETAILS_FAILED,
    ADD_BANK_USER_DETAIL,
    ADD_BANK_USER_DETAIL_SUCCESS,
    ADD_BANK_USER_DETAIL_FAILED,
    EDIT_BANK_USER_DETAIL,
    EDIT_BANK_USER_DETAIL_SUCCESS,
    EDIT_BANK_USER_DETAIL_FAILED,
    DELETE_BANK_USER_DETAIL,
    DELETE_BANK_USER_DETAIL_SUCCESS,
    DELETE_BANK_USER_DETAIL_FAILED,
    SEARCH_BANK_USER_DETAILS,
    FILTER_BANK_USER_TYPE,
    FILTER_BANK_USER_STATUS,
    CLEAR_BANK_USER_FILTERS,
    CLEAR_BANK_USER_ERROR
} from '../store/types';

const initialState = {
    data: [],
    filteredData: [],
    loading: false,
    error: null,
    saving: false,
    saveSuccess: false,
    searchTerm: '',
    filterUserType: '',
    filterStatus: '',
    deleting: false,
    deleteSuccess: false
};

// Helper to normalize user type variants to a canonical form
const normalizeUserType = (value) => {
    if (!value) return '';
    const v = String(value).toLowerCase();
    // Treat common synonyms as the same group
    if ([
        'user', 'customer', 'passenger', 'rider', 'client', 'usuario', 'users'
    ].includes(v)) {
        return 'user';
    }
    if ([
        'driver', 'conductor', 'chofer', 'drivers'
    ].includes(v)) {
        return 'driver';
    }
    return v;
};

// Helper function to filter data
const filterData = (data, searchTerm, filterUserType, filterStatus) => {
    return data.filter(item => {
        // Search filter
        const matchesSearch = !searchTerm || (
            item.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.bankName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.accountNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.userType?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        // User type filter
        const normalizedItemUserType = normalizeUserType(item.userType);
        const normalizedFilterUserType = normalizeUserType(filterUserType);
        const matchesUserType = !normalizedFilterUserType || normalizedItemUserType === normalizedFilterUserType;
        
        // Status filter
        const matchesStatus = !filterStatus || item.status === filterStatus;
        
        return matchesSearch && matchesUserType && matchesStatus;
    });
};

export const bankUserReducer = (state = initialState, action) => {
    switch (action.type) {
        case FETCH_BANK_USER_DETAILS:
            return {
                ...state,
                loading: true,
                error: null
            };
        case FETCH_BANK_USER_DETAILS_SUCCESS:
            const filteredData = filterData(action.payload, state.searchTerm, state.filterUserType, state.filterStatus);
            return {
                ...state,
                loading: false,
                data: action.payload,
                filteredData: filteredData,
                error: null
            };
        case FETCH_BANK_USER_DETAILS_FAILED:
            return {
                ...state,
                loading: false,
                error: action.payload
            };
        case ADD_BANK_USER_DETAIL:
            return {
                ...state,
                saving: true,
                saveSuccess: false,
                error: null
            };
        case ADD_BANK_USER_DETAIL_SUCCESS:
            const newData = [...state.data, action.payload];
            const newFilteredData = filterData(newData, state.searchTerm, state.filterUserType, state.filterStatus);
            return {
                ...state,
                saving: false,
                saveSuccess: true,
                data: newData,
                filteredData: newFilteredData,
                error: null
            };
        case ADD_BANK_USER_DETAIL_FAILED:
            return {
                ...state,
                saving: false,
                saveSuccess: false,
                error: action.payload
            };
        case EDIT_BANK_USER_DETAIL:
            return {
                ...state,
                saving: true,
                saveSuccess: false,
                error: null
            };
        case EDIT_BANK_USER_DETAIL_SUCCESS:
            const updatedData = state.data.map(item => 
                item.id === action.payload.id ? { ...item, ...action.payload } : item
            );
            const updatedFilteredData = filterData(updatedData, state.searchTerm, state.filterUserType, state.filterStatus);
            return {
                ...state,
                saving: false,
                saveSuccess: true,
                data: updatedData,
                filteredData: updatedFilteredData,
                error: null
            };
        case EDIT_BANK_USER_DETAIL_FAILED:
            return {
                ...state,
                saving: false,
                saveSuccess: false,
                error: action.payload
            };
        case DELETE_BANK_USER_DETAIL:
            return {
                ...state,
                deleting: true,
                deleteSuccess: false,
                error: null
            };
        case DELETE_BANK_USER_DETAIL_SUCCESS:
            const remainingData = state.data.filter(item => item.id !== action.payload);
            const remainingFilteredData = filterData(remainingData, state.searchTerm, state.filterUserType, state.filterStatus);
            return {
                ...state,
                deleting: false,
                deleteSuccess: true,
                data: remainingData,
                filteredData: remainingFilteredData,
                error: null
            };
        case DELETE_BANK_USER_DETAIL_FAILED:
            return {
                ...state,
                deleting: false,
                deleteSuccess: false,
                error: action.payload
            };
        case SEARCH_BANK_USER_DETAILS:
            const searchFilteredData = filterData(state.data, action.payload, state.filterUserType, state.filterStatus);
            return {
                ...state,
                searchTerm: action.payload,
                filteredData: searchFilteredData
            };
        case FILTER_BANK_USER_TYPE:
            const typeFilteredData = filterData(state.data, state.searchTerm, action.payload, state.filterStatus);
            return {
                ...state,
                filterUserType: action.payload,
                filteredData: typeFilteredData
            };
        case FILTER_BANK_USER_STATUS:
            const statusFilteredData = filterData(state.data, state.searchTerm, state.filterUserType, action.payload);
            return {
                ...state,
                filterStatus: action.payload,
                filteredData: statusFilteredData
            };
        case CLEAR_BANK_USER_FILTERS:
            const clearedFilteredData = filterData(state.data, '', '', '');
            return {
                ...state,
                searchTerm: '',
                filterUserType: '',
                filterStatus: '',
                filteredData: clearedFilteredData
            };
        case CLEAR_BANK_USER_ERROR:
            return {
                ...state,
                error: null,
                saveSuccess: false,
                deleteSuccess: false
            };
        default:
            return state;
    }
};

export default bankUserReducer;